/**
 * Secure session storage for non-sensitive auth metadata.
 *
 * ## What goes where
 *
 * | Data              | Storage          | Why                                              |
 * |-------------------|------------------|--------------------------------------------------|
 * | JWT (auth token)  | httpOnly cookie  | Set/cleared by backend; JS can never read it.    |
 * | user_role         | sessionStorage   | Needed for UI routing; not secret.               |
 * | user_id / email   | sessionStorage   | Needed for UI display; not secret.               |
 * | user_permissions  | sessionStorage   | Needed for UI access control; not secret.        |
 * | impersonation_*   | sessionStorage   | Session-scoped; less critical than the JWT.      |
 *
 * ## Why sessionStorage?
 * - Cleared automatically when the tab is closed (smaller attack window).
 * - Not shared across tabs.
 * - Page refreshes keep the session alive.
 *
 * ## Migration
 * On first load we clean any residual auth keys from localStorage
 * (left over from the previous localStorage-based implementation).
 */

const AUTH_KEYS = [
  'auth_token',
  'admin_token',
  'user_role',
  'user_id',
  'user_email',
  'user_first_name',
  'user_last_name',
  'user_permissions',
  'is_impersonating',
  'impersonated_manager_id',
  'impersonated_agent_id',
  'impersonated_staff_id',
  'impersonated_user_id',
  'impersonated_role',
  'impersonation_token',
  'original_role',
];

(function migrateFromLocalStorage() {
  try {
    AUTH_KEYS.forEach((key) => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
      }
    });
  } catch (_) {
    // localStorage might be unavailable in some contexts
  }
})();

const tokenStorage = {
  getItem: (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (_) {
      // sessionStorage might be full or unavailable
    }
  },
  removeItem: (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (_) {
      // noop
    }
  },
};

/**
 * Non-sensitive profile mirrored from LoginResponseDto after login (cookie holds the JWT).
 * Shape matches what components used to read from decoded JWT claims.
 */
export function getSessionUserProfile() {
  const role = tokenStorage.getItem('user_role');
  if (!role) return null;
  const fn = tokenStorage.getItem('user_first_name') || '';
  const ln = tokenStorage.getItem('user_last_name') || '';
  const r = role.toLowerCase();
  return {
    id: tokenStorage.getItem('user_id'),
    email: tokenStorage.getItem('user_email'),
    typeUser: role,
    type_user: r,
    firstName: fn,
    lastName: ln,
    first_name: fn,
    last_name: ln,
  };
}

export function hasSessionAuthMetadata() {
  return !!tokenStorage.getItem('user_role');
}

export default tokenStorage;
